<template>
  <div>
    <!-- Floating TARA button -->
    <div
      v-if="showButton"
      class="tara-fab"
      @click="toggleDrawer"
      :title="drawerOpen ? 'Close TARA' : 'Open TARA — AI Assistant'"
    >
      <div class="tara-fab-inner">
        <svg v-if="!drawerOpen" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="10" rx="2"/>
          <circle cx="12" cy="5" r="2"/>
          <path d="M12 7v4M8 11V9M16 11V9"/>
        </svg>
        <v-icon v-else color="white" size="22">mdi-close</v-icon>
      </div>
      <span class="tara-fab-label" v-if="!drawerOpen">TARA</span>
    </div>

    <!-- Slide-in drawer -->
    <transition name="tara-slide">
      <div v-if="drawerOpen" class="tara-drawer" :class="{ 'tara-drawer--open': drawerOpen }">

        <!-- Drawer header -->
        <div class="tara-drawer-header">
          <div class="tara-drawer-title">
            <div class="tara-avatar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2"/>
                <circle cx="12" cy="5" r="2"/>
                <path d="M12 7v4M8 11V9M16 11V9"/>
              </svg>
            </div>
            <div>
              <div class="tara-name">TARA — Your t2 AI Assistant</div>
              <div class="tara-subtitle">Ask me anything about HR policies</div>
            </div>
          </div>
          <div class="tara-header-actions">
            <!-- Open full page button -->
            <v-btn icon x-small @click="openFullPage" title="Open full page">
              <v-icon size="16" color="white">mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn icon x-small @click="toggleDrawer" title="Close">
              <v-icon size="16" color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Chat messages area -->
        <div class="tara-messages" ref="taraMessages">

          <!-- Welcome -->
          <div v-if="messages.length === 0" class="tara-welcome">
            <p>Hi! I'm <strong>TARA</strong>, your t2 AI Assistant 👋</p>
            <p>Ask me about your HR policies, statutory rules, leave entitlements and more.</p>
            <div class="tara-suggestions">
              <div
                v-for="(s, i) in suggestions"
                :key="i"
                class="tara-suggestion-chip"
                @click="sendSuggestion(s)"
              >{{ s }}</div>
            </div>
          </div>

          <!-- Messages -->
          <div v-for="(msg, i) in messages" :key="i" class="tara-msg-row" :class="msg.role === 'user' ? 'tara-msg-row--user' : 'tara-msg-row--ai'">
            <div v-if="msg.role === 'assistant'" class="tara-msg-avatar">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2"/>
                <circle cx="12" cy="5" r="2"/>
                <path d="M12 7v4M8 11V9M16 11V9"/>
              </svg>
            </div>
            <tara-attrition-card
              v-if="msg.cardType === 'attrition'"
              :data="msg.cardData"
              @ask="sendSuggestion"
              style="max-width:320px;"
            />
            <tara-headcount-card
              v-else-if="msg.cardType === 'headcount'"
              :data="msg.cardData"
              @ask="sendSuggestion"
              style="max-width:340px;"
            />
            <tara-payroll-card
              v-else-if="msg.cardType === 'payroll'"
              :data="msg.cardData"
              @ask="sendSuggestion"
              style="max-width:360px;"
            />
            <tara-payroll-select-card
              v-else-if="msg.cardType === 'payroll_select'"
              :data="msg.cardData"
              @select-payroll="onPayrollSelected"
              style="max-width:360px;"
            />
            <tara-workforce-cost-card
              v-else-if="msg.cardType === 'workforce_cost'"
              :data="msg.cardData"
              @ask="sendSuggestion"
              style="max-width:380px;"
            />
            <tara-absenteeism-card
              v-else-if="msg.cardType === 'absenteeism'"
              :data="msg.cardData"
              @ask="sendSuggestion"
            />
            <div v-else-if="!msg.cardType" class="tara-bubble" :class="[msg.role === 'user' ? 'tara-bubble--user' : 'tara-bubble--ai', msg.limitReached ? 'tara-bubble--limit' : '']">
              {{ msg.content }}
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="loading" class="tara-msg-row tara-msg-row--ai">
            <div class="tara-msg-avatar">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2"/>
                <circle cx="12" cy="5" r="2"/>
                <path d="M12 7v4M8 11V9M16 11V9"/>
              </svg>
            </div>
            <div class="tara-bubble tara-bubble--ai tara-bubble--typing">
              <span></span><span></span><span></span>
            </div>
          </div>

        </div>

        <!-- Input area -->
        <div class="tara-input-area">
          <textarea
            ref="taraInput"
            v-model="inputText"
            class="tara-input"
            placeholder="Ask TARA anything..."
            rows="1"
            :disabled="loading"
            @keydown.enter.exact.prevent="sendMessage"
            @input="autoResize"
          />
          <button
            class="tara-send-btn"
            :disabled="!inputText.trim() || loading"
            @click="sendMessage"
          >
            <v-icon size="18" :color="inputText.trim() && !loading ? 'white' : 'grey lighten-1'">mdi-send</v-icon>
          </button>
        </div>

      </div>
    </transition>

    <!-- Backdrop for mobile -->
    <div v-if="drawerOpen" class="tara-backdrop d-sm-none" @click="toggleDrawer" />

  </div>
</template>

<script>
import TaraAttritionCard from "./TaraAttritionCard.vue";
import TaraHeadcountCard from "./TaraHeadcountCard.vue";
import TaraPayrollCard from "./TaraPayrollCard.vue";
import TaraPayrollSelectCard from "./TaraPayrollSelectCard.vue";
import TaraWorkforceCostCard from "./TaraWorkforceCostCard.vue";
import TaraAbsenteeismCard from "./TaraAbsenteeismCard.vue";

export default {
  name: "TaraFloatingAssistant",
  components: { "tara-attrition-card": TaraAttritionCard, "tara-headcount-card": TaraHeadcountCard, "tara-payroll-card": TaraPayrollCard, "tara-payroll-select-card": TaraPayrollSelectCard, "tara-workforce-cost-card": TaraWorkforceCostCard, "tara-absenteeism-card": TaraAbsenteeismCard },

  data() {
    return {
      drawerOpen: false,
      showButton: false,
      inputText: "",
      loading: false,
      suggestions: [
        "What is our annual leave entitlement?",
        "What is the EPF contribution rate?",
        "Who might be leaving soon?",  /* attrition */
        "What are our public holidays?",
      ],
    };
  },

  computed: {
    messages() {
      return this.$store.state.taraMessages;
    }
  },

  mounted() {
    this.checkPermission();
  },

  methods: {
    checkPermission() {
      try {
        const token = this.$session.get("access_token");
        if (!token) return;
        const sessions = JSON.parse(token);
        if (sessions && sessions.funcaccess && sessions.funcaccess.length > 0) {
          const result = sessions.funcaccess.filter(e => e.funcname === "HR Chat");
          this.showButton = result.length > 0 && result[0].canaccess === "Y";
        }
      } catch (e) {
        this.showButton = false;
      }
    },

    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen;
      if (this.drawerOpen) {
        this.$nextTick(() => {
          if (this.$refs.taraInput) this.$refs.taraInput.focus();
        });
      }
    },

    openFullPage() {
      const lang = this.$route.params.lang || "en";
      // Use path-based navigation to be safe
      this.$router.push("/" + lang + "/aichat").catch(() => {});
      this.drawerOpen = false;
    },

    async onPayrollSelected(selection) {
      try {
        this.loading = true;
        this.$store.commit("addTaraMessage", { role: "user", content: `Check payroll: ${selection.payoutdesc} (${selection.pyyear}/${String(selection.pymonth).padStart(2,'0')})` });
        const res = await this.$store.dispatch("post", {
          url: "/ai/chat",
          payload: {
            messages:        this.messages.filter(m => m.content !== null && m.content !== undefined),
            compnyid:        this.$store.state.compnyid || 0,
            staffid:         this.$store.state.staffId  || this.$store.getters.staffId  || null,
            userid:          this.$store.state.userId   || this.$store.getters.userId   || null,
            payrollPoid:     selection.poid,
            payrollPrevPoid: null,
          },
        });
        if (res.data.status === 200) {
          if (res.data.cardType === "payroll") {
            this.$store.commit("addTaraMessage", { role: "assistant", content: null, cardType: "payroll", cardData: res.data.cardData });
          } else if (res.data.cardType === "payroll_select") {
            this.$store.commit("addTaraMessage", { role: "assistant", content: null, cardType: "payroll_select", cardData: res.data.cardData });
          } else {
            this.$store.commit("addTaraMessage", { role: "assistant", content: res.data.reply || "Unable to retrieve payroll anomalies." });
          }
        } else {
          this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I encountered an error checking the payroll." });
        }
      } catch (err) {
        console.error("[TARA] onPayrollSelected error:", err);
        this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I encountered an error checking the payroll." });
      } finally {
        this.loading = false;
      }
    },
    sendSuggestion(text) {
      this.inputText = text;
      this.sendMessage();
    },

    async sendMessage() {
      const text = this.inputText.trim();
      if (!text || this.loading) return;

      this.$store.commit("addTaraMessage", { role: "user", content: text });
      this.inputText = "";
      this.loading = true;
      this.$nextTick(() => {
        this.scrollToBottom();
        if (this.$refs.taraInput) {
          this.$refs.taraInput.style.height = "auto";
        }
      });

      try {
        const res = await this.$store.dispatch("post", {
          url: "/ai/chat",
          payload: {
            messages: this.messages.filter(m => m.content !== null && m.content !== undefined),
            compnyid: this.$store.state.compnyid || 0,
            staffid: this.$store.state.staffId || this.$store.getters.staffId || null,
            userid: this.$store.state.userId || this.$store.getters.userId || null,
          },
        });

        if (res.data.status === 200) {
          if (res.data.cardType === "attrition") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "attrition",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "headcount") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "headcount",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "payroll") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "payroll",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "payroll_select") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "payroll_select",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "workforce_cost") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "workforce_cost",
              cardData: res.data.cardData,
            });
          } else if (res.data.cardType === "absenteeism") {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: null,
              cardType: "absenteeism",
              cardData: res.data.cardData,
            });
          } else {
            this.$store.commit("addTaraMessage", {
              role: "assistant",
              content: res.data.reply,
              limitReached: res.data.limitReached || false,
            });
          }
        } else {
          this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I encountered an error. Please try again." });
        }
      } catch (err) {
        this.$store.commit("addTaraMessage", { role: "assistant", content: "Sorry, I can't connect right now. Please try again." });
      } finally {
        this.loading = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    scrollToBottom() {
      const el = this.$refs.taraMessages;
      if (el) el.scrollTop = el.scrollHeight;
    },

    autoResize(e) {
      e.target.style.height = "auto";
      e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
    },
  },
};
</script>

<style scoped>
/* Floating button */
.tara-fab {
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4F6BFF 0%, #7C3AED 100%);
  box-shadow: 0 4px 20px rgba(79, 107, 255, 0.45);
  cursor: pointer;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  user-select: none;
}
.tara-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 28px rgba(79, 107, 255, 0.55);
}
.tara-fab-inner { display: flex; align-items: center; justify-content: center; }
.tara-fab-label {
  font-size: 9px;
  color: white;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 1px;
}

/* Drawer */
.tara-drawer {
  position: fixed;
  bottom: 96px;
  right: 28px;
  width: 360px;
  height: 520px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  z-index: 998;
  overflow: hidden;
  border: 1px solid rgba(79, 107, 255, 0.15);
}

/* Drawer header */
.tara-drawer-header {
  background: linear-gradient(135deg, #4F6BFF 0%, #7C3AED 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.tara-drawer-title { display: flex; align-items: center; gap: 10px; }
.tara-avatar {
  width: 34px; height: 34px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.tara-name { color: white; font-weight: 700; font-size: 15px; line-height: 1.2; }
.tara-subtitle { color: rgba(255,255,255,0.75); font-size: 11px; }
.tara-header-actions { display: flex; gap: 2px; }

/* Messages */
.tara-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tara-welcome {
  text-align: center;
  padding: 12px 8px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}
.tara-welcome strong { color: #4F6BFF; }
.tara-suggestions { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin-top: 12px; }
.tara-suggestion-chip {
  background: #f1f5ff;
  color: #4F6BFF;
  border: 1px solid #dde5ff;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s;
}
.tara-suggestion-chip:hover { background: #dde5ff; }

/* Message rows */
.tara-msg-row { display: flex; align-items: flex-end; gap: 6px; }
.tara-msg-row--user { flex-direction: row-reverse; }
.tara-msg-avatar {
  width: 24px; height: 24px; flex-shrink: 0;
  background: linear-gradient(135deg, #4F6BFF, #7C3AED);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.tara-bubble {
  max-width: 78%;
  padding: 9px 13px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
}
.tara-bubble--user {
  background: linear-gradient(135deg, #4F6BFF, #7C3AED);
  color: white;
  border-radius: 14px 14px 2px 14px;
}
.tara-bubble--ai {
  background: #f4f6fb;
  color: #1e293b;
  border-radius: 2px 14px 14px 14px;
}
.tara-bubble--limit {
  background: #FFF3E0 !important;
  border: 1px solid #FF9800;
  color: #E65100;
}

/* Typing indicator */
.tara-bubble--typing {
  display: flex; align-items: center; gap: 4px;
  padding: 12px 16px;
}
.tara-bubble--typing span {
  width: 7px; height: 7px;
  background: #94a3b8;
  border-radius: 50%;
  animation: taraTyping 1.2s infinite ease-in-out;
}
.tara-bubble--typing span:nth-child(2) { animation-delay: 0.2s; }
.tara-bubble--typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes taraTyping {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* Input area */
.tara-input-area {
  border-top: 1px solid #e8ecf4;
  padding: 10px 12px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  background: #fff;
}
.tara-input {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  max-height: 80px;
  overflow-y: auto;
  transition: border-color 0.15s;
}
.tara-input:focus { border-color: #4F6BFF; }
.tara-send-btn {
  width: 36px; height: 36px; flex-shrink: 0;
  background: linear-gradient(135deg, #4F6BFF, #7C3AED);
  border: none; border-radius: 50%;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: opacity 0.15s, transform 0.15s;
}
.tara-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.tara-send-btn:not(:disabled):hover { transform: scale(1.08); }

/* Slide transition */
.tara-slide-enter-active, .tara-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.tara-slide-enter, .tara-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}

/* Mobile backdrop */
.tara-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.3);
  z-index: 997;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .tara-drawer {
    right: 0; bottom: 0;
    width: 100%;
    height: 75vh;
    border-radius: 16px 16px 0 0;
  }
  .tara-fab { bottom: 20px; right: 20px; }
}
</style>